import React, { useState, useEffect } from 'react'
import { useAuthStore } from '../store'
import { apiClient } from '../services/apiClient'

export default function ReferralPage() {
  const user = useAuthStore((state: any) => state.user)
  const [isManager, setIsManager] = useState(false)
  const [promoCode, setPromoCode] = useState('')
  const [stats, setStats] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [bonusHistory, setBonusHistory] = useState<any[]>([])

  useEffect(() => {
    loadReferralData()
  }, [])

  const loadReferralData = async () => {
    setLoading(true)
    try {
      const statsData = await apiClient.getReferralStats()
      setStats(statsData.data)
      setIsManager(statsData.data.is_manager)
      setPromoCode(statsData.data.promo_code || '')

      // Загружаем историю бонусов если менеджер
      if (statsData.data.is_manager) {
        const historyData = await apiClient.getCommissionDetails()
        setBonusHistory(historyData.data.bonus_history || [])
      }
    } catch (err) {
      setError('Не удалось загрузить данные реферальной программы')
    } finally {
      setLoading(false)
    }
  }

  const becomeManager = async () => {
    setLoading(true)
    try {
      const result = await apiClient.becomeManager('')
      setIsManager(true)
      setPromoCode(result.data.promo_code)
      await loadReferralData()
    } catch (err) {
      setError('Не удалось стать менеджером')
    } finally {
      setLoading(false)
    }
  }

  const copyPromoCode = () => {
    navigator.clipboard.writeText(promoCode)
    alert('Код скопирован!')
  }

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-8 mt-10">
        <div className="text-center">Загрузка...</div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-8 mt-10">
      <h1 className="text-3xl font-bold mb-6 text-gray-800">🔗 Реферальная программа</h1>
      
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800">{error}</p>
        </div>
      )}
      
      {!isManager ? (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 mb-6">
          <h2 className="text-xl font-bold text-purple-900 mb-4">Станьте менеджером</h2>
          <p className="text-gray-700 mb-4">
            Получите уникальный реферальный код и начните приглашать администраторов других отелей.
          </p>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
            <h3 className="font-bold text-green-900 mb-3">💰 Бонусы:</h3>
            <ul className="list-disc list-inside text-green-800 space-y-2">
              <li>+3 месяца к вашей подписке за каждого приглашенного администратора</li>
              <li>Бонусы накапливаются без ограничений</li>
              <li>Приглашенные администраторы получают +1 месяц к своей подписке</li>
            </ul>
          </div>
          <button
            onClick={becomeManager}
            disabled={loading}
            className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 disabled:opacity-50"
          >
            {loading ? 'Загрузка...' : 'Стать менеджером'}
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h2 className="text-xl font-bold text-blue-900 mb-4">Ваш реферальный код</h2>
            <div className="flex items-center space-x-4">
              <div className="bg-white border-2 border-blue-300 rounded-lg px-6 py-3 text-2xl font-mono font-bold">
                {promoCode}
              </div>
              <button
                onClick={copyPromoCode}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
              >
                📋 Копировать
              </button>
            </div>
            <p className="text-gray-600 mt-4">
              Поделитесь этим кодом с администраторами других отелей.当他们 используют ваш код при регистрации и оплате подписки, вы получите +3 месяца к своей подписке.
            </p>
          </div>

          {stats && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <h2 className="text-xl font-bold text-green-900 mb-4">📊 Ваша статистика</h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white rounded-lg p-4">
                  <div className="text-3xl font-bold text-green-600">{stats.total_referrals}</div>
                  <div className="text-gray-600">Всего рефералов</div>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <div className="text-3xl font-bold text-green-600">{stats.bonus_months_received}</div>
                  <div className="text-gray-600">Месяцев бонусов получено</div>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <div className="text-lg font-bold text-green-600">
                    {stats.subscription_expires_at ? new Date(stats.subscription_expires_at).toLocaleDateString('ru-RU') : 'N/A'}
                  </div>
                  <div className="text-gray-600">Подписка действует до</div>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <div className="text-3xl font-bold text-green-600">{stats.active_referrals}</div>
                  <div className="text-gray-600">Активных рефералов</div>
                </div>
              </div>
            </div>
          )}

          {bonusHistory.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <h2 className="text-xl font-bold text-yellow-900 mb-4">📜 История бонусов</h2>
              <div className="space-y-3">
                {bonusHistory.map((bonus: any) => (
                  <div key={bonus.id} className="bg-white rounded-lg p-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="font-bold">+{bonus.months_added} месяцев</div>
                        <div className="text-sm text-gray-600">
                          {new Date(bonus.created_at).toLocaleDateString('ru-RU')}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-600">
                          До: {bonus.previous_expires_at ? new Date(bonus.previous_expires_at).toLocaleDateString('ru-RU') : 'N/A'}
                        </div>
                        <div className="text-sm text-gray-600">
                          После: {new Date(bonus.new_expires_at).toLocaleDateString('ru-RU')}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {stats && stats.referrals && stats.referrals.length > 0 && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">👥 Ваши рефералы</h2>
              <div className="space-y-3">
                {stats.referrals.map((ref: any) => (
                  <div key={ref.id} className="bg-white rounded-lg p-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="font-bold">{ref.referred_admin_email || 'Unknown'}</div>
                        <div className="text-sm text-gray-600">{ref.referred_admin_hotel || 'Unknown hotel'}</div>
                      </div>
                      <div className="text-right">
                        <div className={`text-sm font-bold ${ref.bonus_applied ? 'text-green-600' : 'text-yellow-600'}`}>
                          {ref.bonus_applied ? '✅ Бонус применен' : '⏳ Ожидает оплаты'}
                        </div>
                        <div className="text-sm text-gray-600">
                          {new Date(ref.created_at).toLocaleDateString('ru-RU')}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
